// src/shared/components/ui/field.tsx

import type { HTMLAttributes, ReactNode } from 'react'
import { useId } from 'react'

import { cn } from '@/shared/utils/cn'

interface FieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  label?: string
  hint?: string
  error?: string
  required?: boolean
  children: (controlProps: {
    id: string
    'aria-describedby'?: string
    'aria-invalid'?: true
  }) => ReactNode
}

export function Field({ className, label, hint, error, required, children, ...props }: FieldProps) {
  const fieldId = useId()
  const hintId = `${fieldId}-hint`
  const errorId = `${fieldId}-error`
  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className={cn('space-y-1.5', className)} {...props}>
      {label ? (
        <label
          className="block text-xs font-medium text-[var(--color-text-soft)]"
          htmlFor={fieldId}
        >
          {label}
          {required ? <span className="ml-0.5 text-[var(--color-danger)]">*</span> : null}
        </label>
      ) : null}
      {children({
        id: fieldId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })}
      {hint && !error ? (
        <p className="text-xs text-[var(--color-text-muted)]" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="text-xs text-[var(--color-danger-fg)]" id={errorId}>
          {error}
        </p>
      ) : null}
    </div>
  )
}
